import React, { useState } from 'react';
import {
  Input, Row, Col, Button, Modal, Form, message, Avatar,
} from 'antd';
import {
  EyeOutlined, PlusOutlined, EditOutlined, DeleteOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import type { ColumnsType } from 'antd/es/table';
import { useBanks } from '../../hooks/useBanks';
import { DataTable } from '../../components';
import type {
  Bank, CreateBankDto, UpdateBankDto,
} from '../../services/bank.service';

interface BankFormValues {
  tenant_id?: string;
  name: string;
  bin: string;
  code: string;
  logo: string;
  short_name: string;
  swift_code?: string;
  ordering?: number;
}

const getErrorMessage = (error: unknown, fallback: string): string => {
  const maybe = error as { response?: { data?: { message?: string } }; message?: string };
  return maybe?.response?.data?.message || maybe?.message || fallback;
};

const BankList: React.FC = () => {
  const navigate = useNavigate();
  const [searchText, setSearchText] = useState('');
  const [tenantFilter, setTenantFilter] = useState<string>('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Bank | null>(null);
  const [form] = Form.useForm<BankFormValues>();

  const { banks, loading, meta, setFilters, refetch, filters, createBank, updateBank, deleteBank } = useBanks();

  const applyFilters = (overrides: Partial<typeof filters> = {}) => {
    setFilters({
      ...filters,
      page: 1,
      q: searchText || undefined,
      tenant_id: tenantFilter || undefined,
      ...overrides,
    });
  };

  const handleSearch = (value: string) => {
    setSearchText(value);
    applyFilters({ q: value || undefined });
  };

  const handlePageChange = (page: number, pageSize: number) => {
    setFilters({ ...filters, page, limit: pageSize });
  };

  const openCreateModal = () => {
    setEditing(null);
    form.resetFields();
    setModalOpen(true);
  };

  const openEditModal = (bank: Bank) => {
    setEditing(bank);
    form.setFieldsValue({
      tenant_id: bank.tenant_id,
      name: bank.name,
      bin: bank.bin,
      code: bank.code,
      logo: bank.logo,
      short_name: bank.short_name,
      swift_code: bank.swift_code,
      ordering: bank.ordering,
    });
    setModalOpen(true);
  };

  const handleSubmit = async (values: BankFormValues) => {
    try {
      if (editing) {
        const dto: UpdateBankDto = {
          name: values.name,
          bin: values.bin,
          code: values.code,
          logo: values.logo,
          short_name: values.short_name,
          swift_code: values.swift_code,
          ordering: values.ordering,
        };
        await updateBank(editing.bank_id, dto);
        message.success('Cập nhật ngân hàng thành công');
      } else {
        if (!values.tenant_id) {
          message.error('Tenant ID bắt buộc khi tạo ngân hàng');
          return;
        }
        const dto: CreateBankDto = {
          tenant_id: values.tenant_id,
          name: values.name,
          bin: values.bin,
          code: values.code,
          logo: values.logo,
          short_name: values.short_name,
          swift_code: values.swift_code,
          ordering: values.ordering,
        };
        await createBank(dto);
        message.success('Tạo ngân hàng thành công');
      }
      setModalOpen(false);
      form.resetFields();
    } catch (error) {
      message.error(getErrorMessage(error, 'Thao tác thất bại'));
    }
  };

  const handleDelete = (record: Bank) => {
    Modal.confirm({
      title: 'Xoá ngân hàng',
      content: `Xoá vĩnh viễn "${record.name}"? Hành động không thể hoàn tác.`,
      okType: 'danger',
      onOk: async () => {
        try {
          await deleteBank(record.bank_id);
          message.success('Xoá thành công');
        } catch (error) {
          message.error(getErrorMessage(error, 'Xoá thất bại'));
        }
      },
    });
  };

  const columns: ColumnsType<Bank> = [
    {
      title: 'Logo',
      dataIndex: 'logo',
      key: 'logo',
      width: 70,
      render: (logo, r) => logo ? <Avatar src={logo} shape="square" /> : <Avatar shape="square">{r.short_name?.[0] ?? '?'}</Avatar>,
    },
    {
      title: 'Tên / Mã',
      dataIndex: 'name',
      key: 'name',
      render: (name, r) => (
        <div>
          <a onClick={() => navigate(`/banks/${r.bank_id}`)}>{name}</a>
          <div style={{ fontSize: 12, color: '#8c8c8c' }}>{r.code} · {r.short_name}</div>
        </div>
      ),
    },
    { title: 'BIN', dataIndex: 'bin', key: 'bin', width: 110 },
    { title: 'SWIFT', dataIndex: 'swift_code', key: 'swift_code', width: 130 },
    { title: 'Thứ tự', dataIndex: 'ordering', key: 'ordering', width: 90 },
    {
      title: 'Tenant',
      dataIndex: 'tenant_id',
      key: 'tenant_id',
      width: 200,
      render: (id?: string) => id ? <code style={{ fontSize: 11 }}>{id.slice(-8)}</code> : '—',
    },
  ];

  const filterContent = (
    <Row gutter={16}>
      <Col span={8}>
        <Input.Search
          placeholder="Tenant ID"
          allowClear
          value={tenantFilter}
          onChange={(e) => setTenantFilter(e.target.value)}
          onSearch={() => applyFilters()}
        />
      </Col>
    </Row>
  );

  return (
    <>
      <DataTable<Bank>
        title="Ngân hàng (cross-tenant)"
        data={banks}
        columns={columns}
        loading={loading}
        rowKey="bank_id"
        searchable
        searchPlaceholder="Tên / mã / short_name / BIN..."
        onSearch={handleSearch}
        onRefresh={refetch}
        filters={filterContent}
        pagination={{
          current: meta.page,
          pageSize: meta.limit,
          total: meta.total,
          onChange: handlePageChange,
        }}
        onRowClick={(record) => navigate(`/banks/${record.bank_id}`)}
        extraActions={
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreateModal}>
            Thêm ngân hàng
          </Button>
        }
        actionColumn={{
          width: 110,
          fixed: 'right',
          actions: (record) => [
            { key: 'view', label: 'Xem', icon: <EyeOutlined />, onClick: () => navigate(`/banks/${record.bank_id}`) },
            { key: 'edit', label: 'Sửa', icon: <EditOutlined />, onClick: () => openEditModal(record) },
            { key: 'delete', label: 'Xoá', icon: <DeleteOutlined />, danger: true, onClick: () => handleDelete(record) },
          ],
        }}
      />

      <Modal
        title={editing ? 'Sửa ngân hàng' : 'Thêm ngân hàng'}
        open={modalOpen}
        onCancel={() => { setModalOpen(false); form.resetFields(); }}
        onOk={() => form.submit()}
        width={700}
        destroyOnHidden
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          {!editing && (
            <Form.Item name="tenant_id" label="Tenant ID" rules={[{ required: true, message: 'Tenant ID bắt buộc' }]}>
              <Input placeholder="Mongo ObjectId của tenant" />
            </Form.Item>
          )}
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="name" label="Tên đầy đủ" rules={[{ required: true }]}>
                <Input placeholder="VD: Vietcombank" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="short_name" label="Tên rút gọn" rules={[{ required: true }]}>
                <Input placeholder="VD: VCB" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="code" label="Mã" rules={[{ required: true }]}>
                <Input placeholder="VCB" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="bin" label="BIN" rules={[{ required: true }]}>
                <Input placeholder="970436" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="swift_code" label="SWIFT">
                <Input placeholder="BFTVVNVX" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="logo" label="Logo URL" rules={[{ required: true }]}>
            <Input placeholder="https://.../vcb.png" />
          </Form.Item>
          <Form.Item name="ordering" label="Thứ tự hiển thị" initialValue={0}>
            <Input type="number" />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default BankList;
export { BankList };
